import { uiPaper, uiSurface } from "shared/ui/uiSurface"
import { useAuth0 } from "@auth0/auth0-react"
import { Business, Circle, Language, Logout, Shield } from "@mui/icons-material"
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  styled,
  Tooltip,
  Typography,
} from "@mui/material"
import MuiAppBar from "@mui/material/AppBar"
import Toolbar from "@mui/material/Toolbar"
import dayjs from "dayjs"
import "dayjs/locale/fr"
import i18n from "i18next"
import moment from "moment"
import { FC, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useLocation } from "react-router"
import { selectMe } from "src/features/profile"
import mblsLogo from "../../assets/mobilus-logo.png"
import {
  appConstants,
  routeConstants,
} from "../../features/shared-module/constants"
import { useAppSelector } from "../hooks"

const StyledAppBar = styled(MuiAppBar)(({ theme }) => ({
  height: appConstants.mainAppBarHeight,
  backgroundColor: "rgba(255, 255, 255, 0.82)",
  backdropFilter: "blur(12px) saturate(180%)",
  WebkitBackdropFilter: "blur(12px) saturate(180%)",
  backgroundImage: "none",
  borderBottom: `1px solid ${theme.palette.divider}`,
  transition: "background-color 0.3s ease",
}))

export const MainAppbar: FC = () => {
  const me = useAppSelector(selectMe)
  const [drawerOpen, setDrawerOpen] = useState(false)

  return (
    <>
      <StyledAppBar {...uiSurface("UI-DSP-004")} position="fixed" elevation={0}>
        <Toolbar
          sx={{
            minHeight: `${appConstants.mainAppBarHeight}px !important`,
            px: { xs: 2, sm: 3 },
          }}
        >
          <Box
            component="img"
            sx={{ height: 28, width: "auto" }}
            alt="Mobilus"
            src={mblsLogo}
            data-testid="nav-logo"
          />
          <AppbarLinks />
          <Stack
            direction="row"
            alignItems="center"
            marginLeft="auto"
            spacing={1.5}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ display: { xs: "none", md: "block" } }}
            >
              {me?.hauler.name}
            </Typography>
            <Box sx={{ position: "relative", display: "inline-flex" }}>
              <Avatar
                data-testid="nav-avatar-btn"
                sx={(theme) => ({
                  width: 36,
                  height: 36,
                  fontSize: 13,
                  fontWeight: 600,
                  bgcolor: theme.palette.primary.main,
                  cursor: "pointer",
                  transition: "box-shadow 0.2s",
                  "&:hover": {
                    boxShadow: `0 0 0 3px ${theme.palette.primary.light}`,
                  },
                })}
                onClick={() => setDrawerOpen(true)}
              >
                {me?.user?.firstName?.[0]?.toUpperCase()}
                {me?.user?.lastName?.[0]?.toUpperCase()}
              </Avatar>
              <WsConnectionDot />
            </Box>
          </Stack>
        </Toolbar>
      </StyledAppBar>
      <Drawer
        data-testid="nav-user-drawer"
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        slotProps={{
          paper: {
            ...uiPaper("UI-DSP-006"),
            sx: {
              width: 320,
              borderRadius: "12px 0 0 12px",
            },
          },
        }}
      >
        <UserDrawerContent onClose={() => setDrawerOpen(false)} />
      </Drawer>
    </>
  )
}

const ROLE_I18N_KEYS: Record<string, string> = {
  ROLE_HAULER_ADMIN: "roleAdmin",
  ROLE_HAULER_DISPATCHER: "roleDispatcher",
  ROLE_HAULER_MANAGER: "roleManager",
  ROLE_ADMIN: "roleAdmin",
}

const WS_STATUS_CONFIG: Record<string, { i18nKey: string; color: string }> = {
  connected: { i18nKey: "wsConnected", color: "#4caf50" },
  connecting: { i18nKey: "wsConnecting", color: "#ff9800" },
  disconnected: { i18nKey: "wsDisconnected", color: "#9e9e9e" },
  error: { i18nKey: "wsError", color: "#f44336" },
}

const UserDrawerContent: FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation()
  const { logout } = useAuth0()
  const me = useAppSelector(selectMe)
  const connectionStatus = useAppSelector((state) => state.ws.connectionStatus)
  const [language, setLanguage] = useState(
    () => localStorage.getItem("language") || "fr",
  )

  const handleLanguageChange = (newLanguage: string) => {
    setLanguage(newLanguage)
    localStorage.setItem("language", newLanguage)
    i18n.changeLanguage(newLanguage)
    dayjs.locale(newLanguage === "fr" ? "fr" : "en")
    moment.locale(newLanguage === "fr" ? "fr" : "en")
  }

  const initials = [me?.user?.firstName?.[0], me?.user?.lastName?.[0]]
    .filter(Boolean)
    .map((c) => c?.toUpperCase() ?? "")
    .join("")

  const fullName = [me?.user?.firstName, me?.user?.lastName]
    .filter(Boolean)
    .join(" ")

  const roleI18nKey = me?.roles
    ?.map((r) => ROLE_I18N_KEYS[r.authority])
    .filter(Boolean)[0]
  const roleLabel = roleI18nKey ? t(roleI18nKey) : undefined

  const wsConfig =
    WS_STATUS_CONFIG[connectionStatus] ?? WS_STATUS_CONFIG.disconnected
  const wsStatus = { label: t(wsConfig.i18nKey), color: wsConfig.color }

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={(theme) => ({
          px: 3,
          pt: 4,
          pb: 3,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        })}
      >
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              fontSize: 24,
              fontWeight: 600,
              bgcolor: "rgba(255,255,255,0.2)",
              color: "#fff",
            }}
          >
            {initials}
          </Avatar>
          {roleLabel && (
            <Chip
              icon={
                <Shield sx={{ fontSize: 14, color: "inherit !important" }} />
              }
              label={roleLabel}
              size="small"
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.7rem",
                height: 24,
                "& .MuiChip-icon": { ml: 0.5 },
              }}
            />
          )}
        </Stack>
        <Typography
          variant="h2"
          sx={{ color: "#fff", fontWeight: 600, lineHeight: 1.3 }}
        >
          {fullName}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "rgba(255,255,255,0.75)", mt: 0.5 }}
        >
          {me?.user?.username}
        </Typography>
      </Box>

      {/* Hauler info */}
      {me?.hauler?.name && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={(theme) => ({
            px: 3,
            py: 1.5,
            backgroundColor: theme.palette.action.hover,
            borderBottom: `1px solid ${theme.palette.divider}`,
          })}
        >
          <Business sx={{ fontSize: 18, color: "text.secondary" }} />
          <Stack sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {me.hauler.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID {me.hauler.id}
            </Typography>
          </Stack>
        </Stack>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, px: 3, pt: 3 }}>
        {/* Language */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Language sx={{ fontSize: 18, color: "text.secondary" }} />
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {t("preferredLang")}
          </Typography>
        </Stack>
        <Select
          data-testid="nav-language-select"
          variant="outlined"
          value={language}
          size="small"
          onChange={(e) => handleLanguageChange(e.target.value as string)}
          fullWidth
          sx={{ borderRadius: 2 }}
        >
          <MenuItem value="en">{t("english")}</MenuItem>
          <MenuItem value="fr">{t("french")}</MenuItem>
        </Select>

        {/* WebSocket status */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 3 }}>
          <Circle sx={{ fontSize: 10, color: wsStatus.color }} />
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {t("liveConnection")}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              ml: "auto !important",
              color: wsStatus.color,
              fontWeight: 600,
            }}
          >
            {wsStatus.label}
          </Typography>
        </Stack>
      </Box>

      <Divider />

      {/* Footer */}
      <Box sx={{ px: 3, py: 2 }}>
        <Button
          data-testid="nav-logout-btn"
          variant="text"
          fullWidth
          startIcon={<Logout sx={{ fontSize: 20 }} />}
          onClick={() => {
            onClose()
            logout({ logoutParams: { returnTo: window.location.origin } })
          }}
          sx={(theme) => ({
            borderRadius: 2,
            py: 1,
            color: theme.palette.error.main,
            textTransform: "none",
            fontWeight: 500,
            "&:hover": {
              backgroundColor: `${theme.palette.error.main}0A`,
            },
          })}
        >
          {t("logout")}
        </Button>
      </Box>
    </Box>
  )
}

const AppbarLinks: FC = () => {
  const { t } = useTranslation()
  const location = useLocation()

  return (
    <List
      {...uiSurface("UI-DSP-005")}
      sx={{
        display: "flex",
        ml: 3,
        gap: 0.5,
      }}
    >
      {appBarLinks.map((item) => {
        const isActive =
          !item.disabled && location.pathname.startsWith(item.path)
        return (
          <ListItemButton
            key={item.path}
            data-testid={`nav-${item.sectionKey}-link`}
            {...(item.disabled ? {} : { component: Link, to: item.path })}
            disabled={item.disabled}
            sx={(theme) => ({
              borderRadius: 99,
              px: 2.5,
              py: 0.75,
              transition: "all 0.2s ease",
              "&.Mui-disabled": {
                opacity: 0.65,
              },
              ...(isActive
                ? {
                    backgroundColor: theme.palette.primary.main,
                    color: "#fff",
                    boxShadow: `0 2px 8px ${theme.palette.primary.main}33`,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.dark,
                    },
                  }
                : {
                    color: theme.palette.text.secondary,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.light,
                      color: theme.palette.primary.dark,
                    },
                  }),
            })}
          >
            <ListItemText
              sx={{
                "& .MuiTypography-root": {
                  fontWeight: isActive ? 600 : 500,
                  fontSize: 13,
                  whiteSpace: "nowrap",
                  color: "inherit",
                },
              }}
              primary={t(item.sectionKey)}
            />
            {item.badgeKey && (
              <Typography
                variant="caption"
                sx={(theme) => ({
                  ml: 1,
                  px: 1,
                  py: 0.125,
                  fontSize: 10,
                  fontWeight: 600,
                  borderRadius: 99,
                  backgroundColor: theme.palette.grey[200],
                  color: theme.palette.text.secondary,
                  whiteSpace: "nowrap",
                  lineHeight: 1.6,
                })}
              >
                {t(item.badgeKey)}
              </Typography>
            )}
          </ListItemButton>
        )
      })}
    </List>
  )
}

const appBarLinks = [
  {
    path: `/${routeConstants.dashboardPath}`,
    sectionKey: "dashboard",
  },
  {
    path: `/${routeConstants.routePlanningPath}`,
    sectionKey: "routePlanning",
    disabled: true,
    badgeKey: "comingSoon",
  },
] as Array<{
  path: string
  sectionKey: string
  disabled?: boolean
  badgeKey?: string
}>

const WsConnectionDot: FC = () => {
  const { t } = useTranslation()
  const connectionStatus = useAppSelector((state) => state.ws.connectionStatus)
  const isConnected = connectionStatus === "connected"
  const wsConfig =
    WS_STATUS_CONFIG[connectionStatus] ?? WS_STATUS_CONFIG.disconnected
  return (
    <Tooltip title={`${t("liveConnection")}: ${t(wsConfig.i18nKey)}`}>
      <Box
        data-testid="ws-connection-dot"
        sx={{
          position: "absolute",
          bottom: 0,
          right: 0,
          width: 10,
          height: 10,
          borderRadius: "50%",
          backgroundColor: wsConfig.color,
          border: "2px solid",
          borderColor: "background.default",
          ...(isConnected && {
            animation: "pulse-ring 2s ease-out infinite",
          }),
        }}
      />
    </Tooltip>
  )
}
